import React, {useCallback, useMemo} from 'react';

import columnDefinitionToIcon from 'notebook/structured-tables/columns/columnDefinitionToIcon';
import makeTableColumnProvenanceUuid, {
  parseTableColumnProvenanceUuid,
} from 'notebook/structured-tables/structured-table-mappings/makeTableColumnProvenanceUuid';
import {
  CompatibleStructuredTableInfo,
  CompatibleColumnInfoMap,
  TableColumnProvenanceUuid,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionValue} from 'ui-library/menus/MenuConstants';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import findDuplicates from 'util/findDuplicates';

interface TableColumnSelectTreeInputProps {
  selectedTableColumnIds: Array<TableColumnProvenanceUuid>;
  setSelectedTableColumnIds: (
    setIdsFunc: (currentSelectedIds: Array<TableColumnProvenanceUuid>) => Array<TableColumnProvenanceUuid>
  ) => void;
  compatibleTableInfos: Array<CompatibleStructuredTableInfo>;
  columnInfoMap: CompatibleColumnInfoMap;
}
/** An input to choose columns from a bunch of table. Only allows selecting one column per table (in the
 * context of Table Mapping, this is a requirement to preserve row integrity across columns) */
export default function TableColumnSelectTreeInput({
  selectedTableColumnIds,
  setSelectedTableColumnIds,
  compatibleTableInfos,
  columnInfoMap,
}: TableColumnSelectTreeInputProps): JSX.Element {
  const treeItems = useMemo(() => makeTableColumnTreeItems(compatibleTableInfos), [compatibleTableInfos]);
  /** When the selected columns are updated, we remove any that are from the same table as newly selected
   * columns so that we only have one column per table.
   */
  const onChangeSelectedIds = useCallback(
    (selectedIds: Array<TableColumnProvenanceUuid>) => {
      setSelectedTableColumnIds((currentSelectedIds: Array<TableColumnProvenanceUuid>) => {
        if (selectedIds.length > currentSelectedIds.length) {
          const newSelectedIds = selectedIds.filter((id) => !currentSelectedIds.includes(id));
          const newSelectedTableUuids = newSelectedIds.map((id) => parseTableColumnProvenanceUuid(id)[0]);
          return currentSelectedIds
            .filter((id) => !newSelectedTableUuids.some((tableUuid) => id.startsWith(tableUuid)))
            .concat(newSelectedIds);
        }
        return selectedIds;
      });
    },
    [setSelectedTableColumnIds]
  );

  /** Remove a single selected column */
  const unselectColumn = useCallback(
    (idToRemove: TableColumnProvenanceUuid) => {
      setSelectedTableColumnIds((currentSelectedIds) => currentSelectedIds.filter((id) => id !== idToRemove));
    },
    [setSelectedTableColumnIds]
  );

  const duplicateColumnNames = useMemo(
    () => findDuplicates(selectedTableColumnIds.map((id) => columnInfoMap[id].structuredTableColumn.name)),
    [columnInfoMap, selectedTableColumnIds]
  );

  return (
    <div>
      <ChipSelectInput
        placeholder=""
        // optionDatas can be left empty because this is a read-only input
        optionDatas={[]}
        isReadOnly={true}
        getChipProps={(tableColumnId: OptionValue) => {
          const columnInfo = columnInfoMap[tableColumnId];
          const columnName = columnInfo.structuredTableColumn.name;
          return {
            // if multiple selected columns have the same name, show the table names to disambiguate
            text: duplicateColumnNames.includes(columnName)
              ? `${columnName} (${columnInfo.structuredTable.name})`
              : columnName,
            onRemove: () => {
              unselectColumn(tableColumnId);
            },
          };
        }}
        value={selectedTableColumnIds}
      />
      <SelectTree
        items={treeItems}
        multiple={true}
        onChangeSelectedIds={onChangeSelectedIds}
        selectedIds={selectedTableColumnIds}
        includeSelectAllItem={false}
        rootItemSortFn={(items) => items}
        isItemSelectable={(item) => item.parentId != null}
      />
    </div>
  );
}

/** Use table infos to create nodes for the SelectTree */
function makeTableColumnTreeItems(
  compatibleTableInfos: Array<CompatibleStructuredTableInfo>
): Array<TreeItem> {
  const tableColumnTreeItems: Array<TreeItem> = [];
  for (const compatibleTable of compatibleTableInfos) {
    const {structuredTable} = compatibleTable;
    const adapter = getStructuredTableAdapterFromTable(structuredTable);
    tableColumnTreeItems.push({
      id: structuredTable.provenanceUuid,
      name: structuredTable.name,
      parentId: null,
      icon: adapter.getTableIcon(structuredTable),
      isInitiallyExpanded: true,
    });
    for (const compatibleColumn of compatibleTable.compatibleStructuredTableColumns) {
      const tableColumnId = makeTableColumnProvenanceUuid(
        structuredTable.provenanceUuid,
        compatibleColumn.structuredTableColumn.provenanceUuid
      );
      tableColumnTreeItems.push({
        id: tableColumnId,
        name: compatibleColumn.structuredTableColumn.name,
        parentId: structuredTable.provenanceUuid,
        icon: columnDefinitionToIcon(compatibleColumn.columnDefinition) ?? 'columns',
      });
    }
  }
  return tableColumnTreeItems;
}
